import {
    formatCurrency,
    formatNumber,
    formatRelativeTime,
    formatTimestamp,
    formatChipText,
} from './components/SharedFrames';
import { resolveChanakyaInverseLivePnl } from './strategyEventFeed';

const sessionWindowFor = (latestActivity, latestLayer) => {
    const sessionStart = latestActivity?.session?.start || latestLayer?.startTime || null;
    const sessionStop = latestActivity?.session?.stop || latestLayer?.stopTime || null;
    return [sessionStart, sessionStop].filter(Boolean).join(' - ') || '—';
};

export function buildVyasaSnapshotMetrics(vyasaMonitor) {
    const vyasaState = vyasaMonitor?.state || {};
    const vyasaCounters = vyasaState.counters || {};
    const vyasaPnl = vyasaState.pnl || {};

    return {
        metrics: [
            {
                label: 'State',
                value: formatChipText(vyasaState.state || '—'),
                tone:
                    vyasaState.state === 'trading' ? 'success'
                        : vyasaState.state === 'armed' ? 'success'
                        : vyasaState.state === 'halted' ? 'error'
                        : vyasaState.state === 'warming_up' ? 'info'
                        : 'default',
                description: vyasaState.stateChangedAt
                    ? `since ${formatRelativeTime(vyasaState.stateChangedAt)}`
                    : null,
            },
            {
                label: 'Open / Cap',
                value: `${vyasaCounters.open ?? 0} / 35`,
                tone: (vyasaCounters.open ?? 0) >= 30 ? 'warning' : 'default',
            },
            {
                label: 'Trades Today',
                value: formatNumber(vyasaCounters.trades, 0),
            },
            {
                label: 'Blocked (book / cap)',
                value: `${vyasaCounters.blockedBook ?? 0} / ${vyasaCounters.blockedCap ?? 0}`,
                description: 'Signals blocked by the 1-lot-per-key rule (book) or by the 35 max-concurrent cap.',
            },
            {
                label: 'Live Orders',
                value: vyasaState.haltReason
                    ? 'HALTED'
                    : (vyasaMonitor?.connected ? 'PAPER (false)' : 'OFFLINE'),
                tone: vyasaState.haltReason ? 'error' : 'default',
            },
            {
                label: 'Last Heartbeat',
                value: formatTimestamp(vyasaState.lastHeartbeatAt),
            },
        ],
        headlineMessage: (() => {
            const vyasaLastEvent = (vyasaMonitor?.events && vyasaMonitor.events[0]) || null;
            const livePnl = (vyasaPnl.realized || 0)
                + (vyasaMonitor?.liveUnrealizedFromLegs ?? vyasaPnl.unrealized ?? 0);
            return vyasaLastEvent?.message
                || `Vyasa ${vyasaState.state || 'initialising'} — ${vyasaCounters.open ?? 0} open / ${vyasaCounters.trades ?? 0} trades today · live P&L ${formatCurrency(livePnl)}`;
        })(),
        stateCommentary: null,
        modelScores: vyasaState.modelScores || null,
        modelScoresAsOf: vyasaState.lastHeartbeatAt || vyasaMonitor?.fetchedAt || null,
    };
}

export function buildMonitorSnapshotView({
    strategyFamily,
    strategyLabel,
    strategyId,
    summary = {},
    latestLayer = null,
    latestActivity = null,
    account = null,
    legs = [],
    positions = [],
}) {
    const sessionWindow = sessionWindowFor(latestActivity, latestLayer);
    const tcaNetPnl = summary.tcaNetPnl ?? summary.netPnl;
    const pnlTone = tcaNetPnl == null ? 'default' : tcaNetPnl < 0 ? 'error' : 'success';

    let metrics;
    if (strategyFamily === 'vidura') {
        // Vidura is a frozen paper lockbox — the gate (clean order-book-mid
        // IV-rank > threshold) is the entire thesis. gate/positions/pnl arrive
        // top-level on /state; defensively read from summary if nested there.
        const gate = summary.gate || {};
        const viduraPositions = summary.positions || {};
        const viduraPnl = summary.pnl || {};
        const ivRank = gate.ivRank;
        const ivThreshold = gate.ivThreshold;
        const inDeployZone = ivRank != null && ivThreshold != null && ivRank > ivThreshold;
        metrics = [
            {
                label: 'Status',
                value: formatChipText(summary.status || 'initialising'),
                tone: summary.status === 'active' ? 'success' : summary.status === 'halted' ? 'error' : 'default',
            },
            {
                label: 'Open condors',
                value: formatNumber(viduraPositions.openCount ?? 0, 0),
                tone: (viduraPositions.openCount ?? 0) > 0 ? 'info' : 'default',
            },
            {
                label: 'IV-rank / threshold',
                value: `${ivRank != null ? formatNumber(ivRank, 2) : '—'} / ${ivThreshold != null ? formatNumber(ivThreshold, 2) : '—'}`,
                tone: inDeployZone ? 'success' : 'default',
                description: 'Clean order-book-mid IV-rank must clear the threshold to deploy.',
            },
            {
                label: 'Last decision',
                value: formatChipText(gate.lastDecision || '—'),
                tone: gate.lastDecision === 'DEPLOY' ? 'success'
                    : gate.lastDecision === 'MISSED' ? 'warning' : 'default',
                description: gate.lastDecisionAt ? `${formatRelativeTime(gate.lastDecisionAt)}` : null,
            },
            {
                label: 'Regime',
                value: formatChipText(gate.regime || '—'),
                tone: gate.regime === 'clean' ? 'success' : gate.regime === 'trans' ? 'warning' : 'default',
            },
            {
                label: 'Realized P&L',
                value: formatCurrency(viduraPnl.realized),
                tone: viduraPnl.realized > 0 ? 'success' : viduraPnl.realized < 0 ? 'error' : 'default',
            },
            {
                label: 'Deploy / Skip / Missed',
                value: `${viduraPnl.deployed ?? 0} / ${viduraPnl.skipped ?? 0} / ${viduraPnl.missed ?? 0}`,
            },
        ];
    } else if (strategyFamily === 'chanakya_inverse') {
        const ciPnl = resolveChanakyaInverseLivePnl(summary, [{ legs }]);
        metrics = [
            {
                label: 'RSI (14)',
                value: (summary.indicators || {}).rsi != null ? formatNumber((summary.indicators || {}).rsi, 1) : '—',
                tone: (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi >= 76 ? 'error'
                    : (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi <= 24 ? 'success' : 'default',
            },
            {
                label: 'Phase',
                value: summary.phase || '—',
                tone: summary.phase === 'ACTIVE' ? 'success' : summary.phase === 'HALTED' ? 'error' : 'default',
            },
            {
                label: 'Open Slots',
                value: Array.isArray(summary.openSlots) && summary.openSlots.length
                    ? summary.openSlots.join(', ')
                    : 'none',
                tone: Array.isArray(summary.openSlots) && summary.openSlots.length ? 'info' : 'default',
            },
            {
                label: 'Active Legs',
                value: formatNumber((legs || []).filter((leg) => (leg?.status || 'active') === 'active').length, 0),
            },
            {
                label: 'Day P&L',
                value: formatCurrency(ciPnl.netPnl),
                tone: ciPnl.netPnl > 0 ? 'success' : ciPnl.netPnl < 0 ? 'error' : 'default',
            },
            {
                label: 'Drawdown',
                value: summary.drawdownPct != null ? `${formatNumber(summary.drawdownPct, 1)}%` : '—',
                tone: summary.drawdownPct > 10 ? 'error' : summary.drawdownPct > 5 ? 'warning' : 'default',
            },
            {
                label: 'Captured',
                value: formatTimestamp(latestLayer?.capturedAt),
            },
        ];
    } else if (strategyFamily === 'chanakya') {
        metrics = [
            {
                label: 'RSI (14)',
                value: (summary.indicators || {}).rsi != null ? formatNumber((summary.indicators || {}).rsi, 1) : '—',
                tone: (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi <= 31 ? 'success'
                    : (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi >= 65 ? 'error' : 'default',
            },
            {
                label: 'Phase',
                value: summary.phase || '—',
                tone: summary.phase === 'ACTIVE' ? 'success' : summary.phase === 'HALTED' ? 'error' : 'default',
            },
            {
                label: 'Eco State',
                value: summary.ecoState || 'IDLE',
                tone: summary.ecoState && summary.ecoState !== 'IDLE' ? 'warning' : 'default',
            },
            {
                label: 'BankNifty Spot',
                value: formatNumber(summary.spotLtp, 0),
            },
            {
                label: 'Filters Active',
                value: [summary.sameDayLoss ? 'Day-Loss Skip' : null, 'Prem 100-600', 'Skip Fri'].filter(Boolean).join(', ') || 'Standard',
                tone: summary.sameDayLoss ? 'warning' : 'default',
            },
            {
                label: 'Captured',
                value: formatTimestamp(latestLayer?.capturedAt),
            },
        ];
    } else {
        metrics = [
            {
                label: 'Layer',
                value: latestLayer?.layerId || '—',
            },
            {
                label: 'Captured',
                value: formatTimestamp(latestLayer?.capturedAt),
            },
            {
                label: 'Session Window',
                value: sessionWindow,
            },
            {
                label: 'Close Underlying',
                value: formatNumber(latestLayer?.closeUnderlying),
            },
            {
                label: 'Account',
                value: summary.accountAlias || summary.accountName || account?.accountAlias || 'prime',
            },
            {
                label: 'Strategy',
                value: strategyLabel,
            },
        ];

        if (strategyId === 'q_cobra_v4') {
            metrics.unshift(
                {
                    label: 'Day P&L',
                    value: formatCurrency(tcaNetPnl),
                    tone: pnlTone,
                },
                {
                    label: 'Status',
                    value: formatChipText(summary.status || 'initialising'),
                },
                {
                    label: 'Regime',
                    value: formatChipText(summary.regime || '—'),
                },
            );
        } else if (strategyId === 'q_cobra_v3') {
            metrics.unshift(
                {
                    label: 'Day P&L',
                    value: formatCurrency(tcaNetPnl),
                    tone: pnlTone,
                },
                {
                    label: 'Signal',
                    value: summary.signalEngine?.side
                        ? `${summary.signalEngine.side} · ${summary.signalEngine.confidence || '—'}`
                        : '—',
                },
            );
        } else {
            metrics.unshift(
                {
                    label: 'Day P&L',
                    value: formatCurrency(tcaNetPnl),
                    tone: pnlTone,
                },
                {
                    label: 'ADX',
                    value: formatNumber(summary.adxValue, 1),
                },
                {
                    label: 'Exposure',
                    value: positions.length ? `${positions.length} positions` : 'Flat',
                    tone: positions.length ? 'primary' : 'default',
                },
            );
        }
    }

    let headlineMessage = summary.lastMessage;
    if (!headlineMessage) {
        if (strategyFamily === 'vidura') {
            const gate = summary.gate || {};
            headlineMessage = `Vidura ${formatChipText(summary.status || 'initialising')} — gate ${gate.lastDecision || 'idle'}${gate.ivRank != null && gate.ivThreshold != null ? ` · IV-rank ${formatNumber(gate.ivRank, 2)} / ${formatNumber(gate.ivThreshold, 2)}` : ''}`;
        } else if (strategyFamily === 'chanakya_inverse') {
            const ciPnl = resolveChanakyaInverseLivePnl(summary, [{ legs }]);
            headlineMessage = `Chanakya Inverse ${summary.phase || 'initialising'} — RSI ${(summary.indicators || {}).rsi != null ? formatNumber((summary.indicators || {}).rsi, 1) : '...'} | Slots: ${Array.isArray(summary.openSlots) && summary.openSlots.length ? summary.openSlots.join(', ') : 'none'} | Day P&L ${formatCurrency(ciPnl.netPnl)}`;
        } else if (strategyFamily === 'chanakya') {
            headlineMessage = `Chanakya ${summary.phase || 'initialising'} — RSI ${(summary.indicators || {}).rsi != null ? formatNumber((summary.indicators || {}).rsi, 1) : '...'} | Eco: ${summary.ecoState || 'IDLE'} | Spot: ${summary.spotLtp ? formatNumber(summary.spotLtp, 0) : '...'}`;
        } else {
            headlineMessage = `Awaiting the first ${strategyLabel} event.`;
        }
    }

    return {
        metrics,
        headlineMessage,
        stateCommentary: summary.stateCommentary && summary.stateCommentary !== summary.lastMessage
            ? summary.stateCommentary
            : null,
        modelScores: null,
        modelScoresAsOf: null,
    };
}

export function snapshotPanelCopy(strategyFamily, executionMode = 'simulation') {
    if (strategyFamily === 'vyasa') {
        return {
            title: 'Vyasa Snapshot',
            description: 'Operational state, position-book + cap counters, paper-trade gate, and last journaled event for Vyasa.',
        };
    }
    if (strategyFamily === 'vidura') {
        return {
            title: 'Vidura Snapshot',
            description: 'Frozen paper lockbox — gate state (clean order-book-mid IV-rank), open condors, decision counts, and realized P&L. Zero conviction, paper-only.',
        };
    }
    if (strategyFamily === 'chanakya_inverse') {
        const engineLabel = executionMode === 'live' ? 'live execution engine' : 'simulation engine';
        return {
            title: 'Chanakya Inverse Snapshot',
            description: `Live RSI, phase, open slots, active legs, and last event from the Chanakya Inverse ${engineLabel}.`,
        };
    }
    if (strategyFamily === 'chanakya') {
        const engineLabel = executionMode === 'live' ? 'live execution engine' : 'simulation engine';
        return {
            title: 'Chanakya Snapshot',
            description: `Live RSI, phase, eco state, active filters, and last event from the Chanakya ${engineLabel}.`,
        };
    }
    return {
        title: 'Strategy Snapshot',
        description: 'Latest commentary, session window, and capture metadata.',
    };
}
